import React, { useState } from 'react';
import { ActivityIcon, AlertTriangleIcon, CheckCircle2Icon, CheckIcon, CircleIcon, ImageOffIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { ControlPointStatus, ControlSectionKind, QualityReport } from '../../types/quality';
import { isMissingPhoto, photosForPoint } from '../../utils/quality';
interface ControlTabProps {
  report: QualityReport;
  onSetPointStatus: (pointId: string, status: ControlPointStatus) => void;
  onRenamePoint: (pointId: string, title: string) => void;
  onDeletePoint: (pointId: string) => void;
  onAddPoint: (sectionId: string, title: string) => void;
  onRenameSection: (sectionId: string, title: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onAddSection: (title: string, kind: ControlSectionKind) => void;
}
export function ControlTab({
  report,
  onSetPointStatus,
  onRenamePoint,
  onDeletePoint,
  onAddPoint,
  onRenameSection,
  onDeleteSection,
  onAddSection
}: ControlTabProps) {
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [newSection, setNewSection] = useState<{
    title: string;
    kind: ControlSectionKind;
  } | null>(null);
  const submitPoint = (sectionId: string) => {
    if (!draftTitle.trim()) return;
    onAddPoint(sectionId, draftTitle.trim());
    setDraftTitle('');
    setAddingTo(null);
  };
  return <div className="space-y-5" data-id="element-491" data-mp="3dplyxsqlkm3r">
      {report.sections.map(section => {
      const missing = section.points.filter(p => isMissingPhoto(report, p)).length;
      const isProcess = section.kind === 'Proceskontrol';
      return <section key={section.id} className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-id="element-492" data-mp="3sltiqry3flii">
            <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-stone-100 px-5 py-4" data-id="element-493" data-mp="12vpt4p4embo3">
              <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${isProcess ? 'bg-brand-50 text-brand-600' : 'bg-stone-100 text-stone-500'}`} data-id="element-494" data-mp="17u14536fqs7w">
                {isProcess ? <ActivityIcon className="h-[18px] w-[18px]" data-id="element-495" data-mp="3cxazuk3fzqrv" /> : <CheckCircle2Icon className="h-[18px] w-[18px]" data-id="element-496" data-mp="38yleo5c5x4xo" />}
              </span>

              <div className="min-w-0 flex-1" data-id="element-497" data-mp="2l64xdx091gzf">
                <input value={section.title} onChange={e => onRenameSection(section.id, e.target.value)} aria-label="Sektionens navn" className="w-full truncate rounded border-none bg-transparent p-0 text-base font-semibold text-stone-900 outline-none focus:bg-stone-50 focus:px-1.5" data-id="element-498" data-mp="2g9o7ax9hosjp" />
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone-500" data-id="element-499" data-mp="1xin05ly7tqp2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium ${isProcess ? 'bg-brand-50 text-brand-700' : 'bg-stone-100 text-stone-600'}`} data-id="element-500" data-mp="1nezvc5qq9l97">
                    <span className={`h-1.5 w-1.5 rounded-full ${isProcess ? 'bg-brand-500' : 'bg-stone-400'}`} data-id="element-501" data-mp="1ieu1ciwzjmny" />
                    {section.kind}
                  </span>
                  <span data-id="element-502" data-mp="2b1y9gwrdkh3y">
                    {section.points.length}{' '}
                    {section.points.length === 1 ? 'kontrolpunkt' : 'kontrolpunkter'}
                  </span>
                </p>
              </div>

              {missing > 0 && <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700" data-id="element-503" data-mp="ku7o6prp8x58">
                  <ImageOffIcon className="h-3.5 w-3.5" data-id="element-504" data-mp="15ycjuptreghp" />
                  {missing} mangler foto
                </span>}

              <button onClick={() => onDeleteSection(section.id)} aria-label={`Slet ${section.title}`} className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-505" data-mp="1162v3d4drmh0">
                <Trash2Icon className="h-4 w-4" data-id="element-506" data-mp="1ovx5zp5530ym" />
              </button>
            </header>

            <ul className="divide-y divide-stone-100" data-id="element-507" data-mp="1jxlxpiuhsyii">
              {section.points.map(point => {
            const missingPhoto = isMissingPhoto(report, point);
            const photoCount = photosForPoint(report, point.id).length;
            const isOk = point.status === 'ok';
            const isDeviation = point.status === 'deviation';
            return <li key={point.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5" data-id="element-508" data-mp="3upf0n1ufplc1">
                    {isOk ? <CheckCircle2Icon className="h-5 w-5 flex-shrink-0 text-emerald-500" data-id="element-509" data-mp="2f6fr7yaw20if" /> : isDeviation ? <AlertTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-500" data-id="element-510" data-mp="2k4h8qeso1rrs" /> : <CircleIcon className="h-5 w-5 flex-shrink-0 text-stone-300" data-id="element-511" data-mp="2oy3ctikrknbz" />}

                    <div className="min-w-0 flex-1" data-id="element-512" data-mp="2txy1dfh2h0tv">
                      <input value={point.title} onChange={e => onRenamePoint(point.id, e.target.value)} aria-label="Kontrolpunktets navn" className={`w-full truncate rounded border-none bg-transparent p-0 text-sm font-medium outline-none focus:bg-stone-50 focus:px-1.5 ${isOk ? 'text-stone-500' : 'text-stone-900'}`} data-id="element-513" data-mp="2xwfb86rvgpez" />
                      {point.note && <p className="mt-0.5 truncate text-xs text-stone-500" data-id="element-514" data-mp="11fu4re96al21">
                          {point.note}
                        </p>}
                    </div>

                    {missingPhoto ? <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600" data-id="element-515" data-mp="1aefkz2r69nzn">
                        <ImageOffIcon className="h-3.5 w-3.5" data-id="element-516" data-mp="1odyt3vjzgzby" />
                        mangler foto
                      </span> : photoCount > 0 && <span className="text-xs text-stone-400" data-id="element-517" data-mp="1y4tekxpenuwc">
                          {photoCount} foto
                        </span>}

                    <div className="flex flex-shrink-0 items-center gap-1" data-id="element-518" data-mp="284k7ww28c3uu">
                      <button onClick={() => onSetPointStatus(point.id, isDeviation ? 'pending' : 'deviation')} aria-pressed={isDeviation} title="Marker afvigelse" className={`rounded-lg p-2 transition-colors ${isDeviation ? 'bg-red-50 text-red-600' : 'text-stone-300 hover:bg-red-50 hover:text-red-600'}`} data-id="element-519" data-mp="235h6rmpdehs5">
                        <AlertTriangleIcon className="h-4 w-4" data-id="element-520" data-mp="1u1sgy5sf9a7n" />
                      </button>

                      {isOk ? <button onClick={() => onSetPointStatus(point.id, 'pending')} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-50" data-id="element-521" data-mp="1z1yg0710tyzr">
                          <CheckIcon className="h-3.5 w-3.5" data-id="element-522" data-mp="24jfn0rs6mea9" />
                          OK
                        </button> : <button onClick={() => onSetPointStatus(point.id, 'ok')} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-523" data-mp="1vk29no0xt5k9">
                          <PlusIcon className="h-3.5 w-3.5" data-id="element-524" data-mp="1gvefnm6qp3wf" />
                          Registrer
                        </button>}

                      <button onClick={() => onDeletePoint(point.id)} aria-label={`Slet ${point.title}`} className="rounded-lg p-2 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-525" data-mp="1ctsz9quzy7hh">
                        <Trash2Icon className="h-4 w-4" data-id="element-526" data-mp="xx2824vh822y" />
                      </button>
                    </div>
                  </li>;
          })}
            </ul>

            <div className="border-t border-stone-100 px-5 py-3" data-id="element-527" data-mp="sq54i5v4wnog">
              {addingTo === section.id ? <div className="flex flex-wrap items-center gap-2" data-id="element-528" data-mp="xr2qyun6g3vr">
                  <input autoFocus value={draftTitle} onChange={e => setDraftTitle(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') submitPoint(section.id);
              if (e.key === 'Escape') {
                setAddingTo(null);
                setDraftTitle('');
              }
            }} placeholder="Navn på kontrolpunkt…" className="min-w-0 flex-1 rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-529" data-mp="12pvzs9rir6pl" />
                  <button onClick={() => submitPoint(section.id)} className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-530" data-mp="3o3ftn5hfe48n">
                    Tilføj
                  </button>
                  <button onClick={() => {
              setAddingTo(null);
              setDraftTitle('');
            }} className="rounded-lg px-3 py-2 text-sm font-medium text-stone-500 transition-colors hover:bg-stone-100" data-id="element-531" data-mp="5xb7mznribil">
                    Annuller
                  </button>
                </div> : <button onClick={() => {
            setAddingTo(section.id);
            setDraftTitle('');
          }} className="inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-brand-700" data-id="element-532" data-mp="3j4l8hwvzk3ld">
                  <PlusIcon className="h-4 w-4" data-id="element-533" data-mp="2j37q3obgck1c" />
                  Tilføj kontrolpunkt
                </button>}
            </div>
          </section>;
    })}

      {/* Add section */}
      {newSection ? <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-534" data-mp="2a4w2lv5vu2d4">
          <div className="flex items-center justify-between gap-3" data-id="element-535" data-mp="253gl2d2wydzh">
            <h3 className="text-base font-semibold text-stone-900" data-id="element-536" data-mp="2055b82cmc3h9">
              Ny kontrolsektion
            </h3>
            <button onClick={() => setNewSection(null)} aria-label="Annuller" className="rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-537" data-mp="1vddgrgqcddlu">
              <XIcon className="h-4 w-4" data-id="element-538" data-mp="1mbnpcawgmeo2" />
            </button>
          </div>

          <input autoFocus value={newSection.title} onChange={e => setNewSection({
        ...newSection,
        title: e.target.value
      })} placeholder="f.eks. Tagkonstruktion, slutkontrol" className="mt-4 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-539" data-mp="1hdm4pljdy7ah" />

          <div className="mt-3 flex gap-2" data-id="element-540" data-mp="xslyviy1s1qm">
            {(['Proceskontrol', 'Slutkontrol'] as ControlSectionKind[]).map(kind => <button key={kind} onClick={() => setNewSection({
          ...newSection,
          kind
        })} aria-pressed={newSection.kind === kind} className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${newSection.kind === kind ? 'bg-brand-600 text-white' : 'border border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-541" data-mp="1ljhl80jqi4qh">
                  {kind}
                </button>)}
          </div>

          <button onClick={() => {
        if (!newSection.title.trim()) return;
        onAddSection(newSection.title.trim(), newSection.kind);
        setNewSection(null);
      }} className="mt-4 w-full rounded-lg bg-stone-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-542" data-mp="3twv5azhb5op0">
            Opret sektion
          </button>
        </div> : <button onClick={() => setNewSection({
      title: '',
      kind: 'Proceskontrol'
    })} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-stone-300 bg-white px-4 py-4 text-sm font-medium text-stone-500 transition-colors hover:border-brand-300 hover:text-brand-700" data-id="element-543" data-mp="3b15258xn2m9j">
          <PlusIcon className="h-4 w-4" data-id="element-544" data-mp="z8zsuh1tzix9" />
          Tilføj kontrolsektion
        </button>}
    </div>;
}